import React, { useState, useRef, useCallback } from 'react';
import { CheckCircleIcon } from '../../icons';
import { colors } from '../../styles/colors';
import {
  Container,
  SelectButton,
  SelectText,
  SelectIcon,
  DropdownList,
  DropdownItem,
  StyledArrowIcon,
} from './styles';

interface Option {
  label: string;
  value: any;
}

interface CustomSelectProps {
  placeholder?: string;
  options: Option[];
  value?: Option | null;
  onSelect?: (option: Option) => void;
  disabled?: boolean;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  placeholder,
  options,
  value,
  onSelect,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option: Option) => {
    onSelect?.(option);
    setIsOpen(false);
  };

  return (
    <Container ref={containerRef}>
      <SelectButton
        onClick={handleToggle}
        disabled={disabled}
        isOpen={isOpen}
      >
        <SelectText>
          {value ? value.label : placeholder}
        </SelectText>
        <SelectIcon isOpen={isOpen}>
          <StyledArrowIcon isOpen={isOpen} color={colors.text['primary-base']} />
        </SelectIcon>
      </SelectButton>

      {isOpen && (
        <DropdownList>
          {options.map((option, index) => {
            const isSelected = value?.value === option.value;
            return (
              <DropdownItem
                key={index}
                onClick={() => handleSelect(option)}
                isSelected={isSelected}
              >
                {option.label}
                {isSelected && <CheckCircleIcon />}
              </DropdownItem>
            );
          })}
        </DropdownList>
      )}
    </Container>
  );
};
